import { Link } from "react-router-dom";
import { UserCheck, ClipboardCheck, BadgeCheck, LayoutDashboard, Wallet, LifeBuoy } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHead, BadgeLive, BtnOrange, BtnGhost } from "@/components/bits";
import { usePreRegister } from "@/components/PreRegisterModal";
import { IMAGES } from "@/data/site";

const CATS = [
  {
    name: "Básico",
    desc: "Uma alternativa prática para deslocamentos cotidianos, combinando conforto, conveniência e segurança.",
  },
  {
    name: "Select",
    desc: "Uma categoria para quem procura uma experiência diferenciada e um padrão superior de conforto.",
  },
  {
    name: "Premium",
    desc: "Categoria de padrão elevado, voltada a uma experiência mais exclusiva de conforto e atendimento.",
  },
];

const PASS_BENEFITS = [
  "Solicitação de corridas pelo aplicativo",
  "Informações da viagem",
  "Diferentes categorias",
  "Acompanhamento do motorista",
  "Recursos de segurança",
  "Formas de pagamento disponíveis no app",
];

const DRIVER_BENEFITS = [
  "Cadastro digital",
  "Análise e aprovação",
  "Categorias de atuação",
  "Gerenciamento da operação",
  "Consulta de informações financeiras",
  "Suporte e recursos de segurança",
];

export default function Taxi360() {
  const { openPreRegister } = usePreRegister();

  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Serviço principal · Primeira fase"
        title="360Taxi — Sua próxima experiência de mobilidade."
        lead="O 360Taxi é a solução de mobilidade urbana do ecossistema OFF360. Criado para conectar passageiros e motoristas parceiros, o serviço oferece categorias de corridas e uma experiência pensada para unir conforto, tecnologia, praticidade e segurança."
        image={IMAGES.driverNav}
        imageAlt="Motorista parceiro dirigindo em ambiente urbano"
      />

      <section className="mx-auto max-w-8xl px-4 py-20 sm:px-6 md:py-24 lg:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Reveal className="h-full">
            <div className="h-full rounded-3xl border border-slate-200 bg-slate-50 p-8 sm:p-10">
              <div className="flex items-center justify-between gap-4">
                <h2 className="font-display text-2xl font-bold text-slate-900">Para passageiros</h2>
                <BadgeLive>Operação 360Taxi</BadgeLive>
              </div>
              <ul className="mt-6 space-y-3" data-testid="beneficios-passageiros">
                {PASS_BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-base text-slate-700">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                    {b}
                  </li>
                ))}
              </ul>
              <BtnGhost as={Link} to="/passageiros" dark={false} className="mt-8">
                Ver página de passageiros
              </BtnGhost>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="h-full">
            <div className="h-full rounded-3xl border border-slate-200 bg-slate-50 p-8 sm:p-10">
              <div className="flex items-center justify-between gap-4">
                <h2 className="font-display text-2xl font-bold text-slate-900">Para motoristas</h2>
                <BadgeLive>Operação 360Taxi</BadgeLive>
              </div>
              <ul className="mt-6 space-y-3" data-testid="beneficios-motoristas">
                {DRIVER_BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-base text-slate-700">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
                    {b}
                  </li>
                ))}
              </ul>
              <BtnGhost as={Link} to="/motoristas" dark={false} className="mt-8">
                Ver página de motoristas
              </BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-24" data-testid="categorias-360taxi">
        <div className="mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHead
              eyebrow="Categorias de corrida"
              title="Três experiências, um mesmo padrão de cuidado."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {CATS.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-orange to-brand-amber font-display text-lg font-extrabold text-white">
                    {i + 1}
                  </span>
                  <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">{c.name}</h3>
                  <p className="mt-3 text-base leading-relaxed text-slate-600">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
              <h3 className="font-display text-lg font-bold text-slate-900">Regras de classificação</h3>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-600 sm:text-base">
                <li>• O sistema determina a categoria do motorista durante o processo de cadastro e validação.</li>
                <li>• O motorista não escolhe livremente sua categoria.</li>
                <li>• A disponibilidade de categorias depende da região e da operação.</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-8xl px-4 pb-24 pt-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grain relative overflow-hidden rounded-3xl bg-brand-obsidian px-6 py-14 sm:px-12">
            <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
              <div className="max-w-xl">
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-brand-orange">
                  Pré-cadastro
                </p>
                <h2 className="mt-3 font-display text-2xl font-extrabold text-white sm:text-3xl">
                  Prepare-se para o lançamento do 360Taxi.
                </h2>
                <p className="mt-3 text-base text-slate-300">
                  Deixe seus dados para receber um aviso quando o aplicativo estiver disponível para download.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <BtnOrange
                  data-testid="taxi360-cta-passageiro"
                  onClick={() => openPreRegister({ interesse: "Passageiro 360Taxi", origem: "360taxi" })}
                >
                  <UserCheck className="h-4 w-4" /> Quero ser avisado
                </BtnOrange>
                <BtnGhost
                  as={Link}
                  to="/motoristas"
                  data-testid="taxi360-link-motorista"
                  className="!bg-transparent"
                >
                  <ClipboardCheck className="h-4 w-4" /> Seja motorista parceiro
                </BtnGhost>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

export const TaxiIcons = { BadgeCheck, LayoutDashboard, Wallet, LifeBuoy };
